import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ahuBoardApi } from 'app/services/ahuBoardApi';
import {
  useDeleteAhuSectionMutation,
  useBulkDeleteSectionsMutation,
} from 'app/services/ahuBoardFormsApi';
import { clearSelection, selectSection } from 'app/slices/ahuBoardSlice';
import type { IAhuBoardSection, IAhuBoardTunnel } from 'models';
import { ahuBoardBus } from '../bus/ahuBoardBus';
import { removeErrorsAfterTunnelDeleting } from '../validation/validationController';
import {
  beginCanvasRedrawBatch,
  holdCanvasRedrawBatchFor,
} from '../three/canvasRedrawScheduler';

/**
 * section.toolbar.js:413/:315 — the delete cascade's settling delay before
 * `validate:tunnel:after:section:destroy`, which is the pass that takes the
 * section off the canvas (legacy's only one).
 */
const SECTION_DESTROY_DELAY_MS = 500;

/**
 * Phase 6e1 — section delete/duplicate chrome (§8.5, section.toolbar.js +
 * section.toolbar.tmpl, markup faithful): the hidden #duplicate_section /
 * #delete_section buttons (toolbar lis label-forward to them), the Delete
 * Section popup with wording variants (single / multi "Sections" / ER Wheel
 * "both Sections"), Delete-key handler (body keyup 46 outside inputs, gated
 * on `body.ahu-access-permission`), `show:delete:popup` / `show:duplicate:popup`
 * consumers, and the single + bulk delete cascades.
 *
 * [NEG]#43 honored: no error_log_ctrl/section_ctrl change handlers.
 */

interface SectionToolbarChromeProps {
  ahuId: number;
  tunnels: IAhuBoardTunnel[];
  sections: IAhuBoardSection[];
  /** Opens the insert popup in duplicate mode (§9.2 — InsertSectionPopup). */
  onDuplicateSection: (section: IAhuBoardSection) => void;
}

const SectionToolbarChrome = ({
  ahuId,
  tunnels,
  sections,
  onDuplicateSection,
}: SectionToolbarChromeProps) => {
  const dispatch = useAppDispatch();
  const [deleteSection] = useDeleteAhuSectionMutation();
  const [bulkDelete] = useBulkDeleteSectionsMutation();
  const currentSectionId = useAppSelector((s) => s.ahuBoard.selection.currentSectionId);
  const selectedIds = useAppSelector((s) => s.ahuBoard.selection.selectedSectionIds);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupSectionId, setPopupSectionId] = useState<number | null>(null);
  const [dupError, setDupError] = useState<string | null>(null);

  const currentSection = sections.find((s) => s.id === currentSectionId);
  const popupSection = sections.find((s) => s.id === (popupSectionId ?? currentSectionId));
  const multi = selectedIds.length > 1;
  const boundSectionOf = (section: IAhuBoardSection | undefined) =>
    section
      ? sections.find(
          (s) => s.sitting_on_id === section.id || s.id === section.sitting_on_id
        )
      : undefined;

  // Toolbar #sections-ul arming + `.section_info` text (§8.5).
  useEffect(() => {
    const ul = document.getElementById('sections-ul');
    if (!ul) return;
    ul.querySelectorAll('li').forEach((li) =>
      li.classList.toggle('non-clickable', !currentSection)
    );
    ul.querySelectorAll<HTMLElement>('span.section_info').forEach((el) => {
      el.textContent = currentSection
        ? ` : ${String(currentSection.name ?? '')}${
            currentSection.tag ? ` : ${currentSection.tag}` : ''
          }`
        : '';
    });
    ul.querySelector('.section_info_all')?.classList.toggle('hide', !multi);
  }, [currentSection, multi]);

  const showDeletePopup = (sectionId?: number | null) => {
    setPopupSectionId(sectionId ?? null);
    setPopupOpen(true);
    document.querySelector('.main-rotation')?.classList.add('popup-opened');
  };
  const hideDeletePopup = () => {
    setPopupOpen(false);
    setPopupSectionId(null);
    document.querySelector('.main-rotation')?.classList.remove('popup-opened');
  };

  // show:delete:popup / show:duplicate:popup receivers (context menus §2.4).
  useEffect(() => {
    const offs = [
      ahuBoardBus.on('show:delete:popup', (_acc, sectionId) => {
        showDeletePopup(sectionId ?? null);
      }),
      ahuBoardBus.on('show:duplicate:popup', (sectionId) => {
        const section = sections.find((s) => s.id === sectionId);
        if (section) duplicateSection(section);
      }),
    ];
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);

  // Delete key (body keyup 46 outside inputs/contenteditable, permission-gated).
  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== 'Delete') return;
      if (!document.body.classList.contains('ahu-access-permission')) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      )
        return;
      if (currentSectionId != null) showDeletePopup(null);
    };
    document.body.addEventListener('keyup', onKeyUp);
    return () => document.body.removeEventListener('keyup', onKeyUp);
  }, [currentSectionId]);

  // ---- delete cascades (§8.5) ----
  const afterSectionRemoved = (section: IAhuBoardSection, tunnelRemoved: boolean) => {
    ahuBoardBus.emit(`section:destroy:${section.id}`);
    ahuBoardBus.emit('select:accessory', null);
    ahuBoardBus.emit(`remove_component_esp_${section.id}`);
    if (tunnelRemoved) {
      ahuBoardBus.emit('sections_collection:removed', section.tunnel_id);
      ahuBoardBus.emit('tunnel:removed', section.tunnel_id);
      ahuBoardBus.emit('render_tunnel_lugs');
      void dispatch(removeErrorsAfterTunnelDeleting(section.tunnel_id));
      ahuBoardBus.emit('get:overall:height', true);
    }
    window.setTimeout(() => {
      ahuBoardBus.emit('validate:tunnel:after:section:destroy', {
        sectionId: section.id,
        tunnelId: section.tunnel_id,
        skip: true,
        tunnel_removed: tunnelRemoved,
      });
      ahuBoardBus.emit('validate:tunnel:components:only', {
        tunnelId: section.tunnel_id,
      });
    }, SECTION_DESTROY_DELAY_MS);
  };

  const refetchBoard = () => {
    // Server truth after a delete: the tunnel row (recalculated length) and
    // every tunnel's section list — the server shifts `order_rate` on
    // everything downstream of the gap, and the canvas numbers sections off it
    // (canvas3dGeometry:629). Fetched directly rather than through
    // invalidateTags (same two endpoints, ids matched) so the delete's single
    // canvas pass can wait for them and draw the shifted numbering first time.
    holdCanvasRedrawBatchFor(
      Promise.all([
        dispatch(
          ahuBoardApi.endpoints.getAhuTunnelList.initiate(ahuId, {
            forceRefetch: true,
            subscribe: false,
          })
        ),
        ...tunnels.map((t) =>
          dispatch(
            ahuBoardApi.endpoints.getTunnelSectionList.initiate(t.id, {
              forceRefetch: true,
              subscribe: false,
            })
          )
        ),
      ])
    );
  };

  const doDelete = () => {
    document.querySelector('.deleting-section-note')?.classList.add('active');
    // Close the popup straight away — the delete request runs in the background
    // and the "deleting section..." note is what reports it still being in
    // flight. Everything below reads values captured before this point.
    hideDeletePopup();
    // One canvas pass for the whole delete (canvasRedrawScheduler). Without
    // it the optimistic cache filter redraws the section away while the
    // tunnel around it is still the old length, each re-GET redraws again,
    // and the ported `validate:tunnel:after:section:destroy` pass redraws a
    // third time 500 ms later. Legacy is data-blind and only ever runs that
    // last one (section.toolbar.js:413-425), which is what this restores —
    // the section stays put for the half second, then goes in one clean draw.
    const releaseCanvas = beginCanvasRedrawBatch();
    const finish = () => {
      document.querySelector('.deleting-section-note')?.classList.remove('active');
      refetchBoard();
      // Released just after the destroy pass is requested: `.then` above runs
      // before this `.finally`, so at an equal delay its timer fires first.
      window.setTimeout(releaseCanvas, SECTION_DESTROY_DELAY_MS);
    };

    if (multi) {
      // Bulk delete — collect bound dual ids too (§8.5 / parity 163).
      const ids = new Set<number>(selectedIds);
      selectedIds.forEach((id) => {
        const bound = boundSectionOf(sections.find((s) => s.id === id));
        if (bound) ids.add(bound.id);
      });
      const sectionList = [...ids]
        .map((id) => sections.find((s) => s.id === id))
        .filter((s): s is IAhuBoardSection => !!s);
      const tunnelIds = new Set(sectionList.map((s) => s.tunnel_id));
      const remaining = sections.filter((s) => !ids.has(s.id));
      // Re-select first remaining or full deselect. Done before the request
      // (as legacy does, section.toolbar.js:368) so that with the popup already
      // closed a second Delete press can't re-target a section being deleted.
      if (remaining.length) {
        dispatch(selectSection({ sectionId: remaining[0].id }));
        ahuBoardBus.emit('force:select:section', remaining[0].id);
      } else {
        dispatch(clearSelection());
        ahuBoardBus.emit('remove:selected:section');
      }
      ahuBoardBus.emit('update:multiple:sections:list');
      bulkDelete({ section_ids: [...ids], tunnel_ids: [...tunnelIds] })
        .unwrap()
        .then(() => {
          sectionList.forEach((s) => {
            const tunnelEmpty = !remaining.some((r) => r.tunnel_id === s.tunnel_id);
            afterSectionRemoved(s, tunnelEmpty);
          });
        })
        .catch(() => undefined)
        .finally(finish);
      return;
    }

    const section = popupSection;
    if (!section) {
      finish();
      return;
    }
    const tunnelSections = sections
      .filter((s) => s.tunnel_id === section.tunnel_id)
      .sort((a, b) => (a.order_rate ?? 0) - (b.order_rate ?? 0));
    const idx = tunnelSections.findIndex((s) => s.id === section.id);
    // Airflow-aware neighbor pick (order−2 / order — §8.5).
    const tunnel = tunnels.find((t) => t.id === section.tunnel_id);
    const neighborIdx = tunnel?.airflow_direction_id === 2 ? idx + 1 : idx - 1;
    const neighbor = tunnelSections[neighborIdx] ?? tunnelSections[idx + 1] ?? null;
    const bound = boundSectionOf(section);

    // Selection moves off the section before the request (legacy ordering,
    // section.toolbar.js:368) — see the bulk path above.
    if (neighbor && neighbor.id !== section.id) {
      dispatch(selectSection({ sectionId: neighbor.id }));
      ahuBoardBus.emit('force:select:section', neighbor.id);
    } else {
      dispatch(clearSelection());
      ahuBoardBus.emit('remove:selected:section');
    }

    deleteSection({ id: section.id, tunnelId: section.tunnel_id })
      .unwrap()
      .then(() => {
        const tunnelEmpty = tunnelSections.length <= 1;
        afterSectionRemoved(section, tunnelEmpty);
        if (bound) {
          ahuBoardBus.emit('validate:tunnel:after:section:destroy', {
            sectionId: bound.sitting_on_id ?? bound.id,
            tunnelId: bound.tunnel_id,
          });
        }
      })
      .catch(() => undefined)
      .finally(finish);
  };

  const duplicateSection = (section: IAhuBoardSection) => {
    const bound = boundSectionOf(section);
    if (bound) {
      // dual-tunnel sections → error popup (§8.5 / parity 164).
      setDupError(String(section.name ?? ''));
      return;
    }
    onDuplicateSection(section);
  };

  return (
    <>
      <div className={`delete-duplicate-wrapper${currentSection ? ' active' : ''}`}>
        <button
          id="duplicate_section"
          data-id={currentSection?.id}
          className={`hide icon-copy ahu-header-buttons  btn-green ka-has-tooltip${
            boundSectionOf(currentSection) ? ' non-clickable' : ''
          }`}
          {...{ tooltip: 'Duplicate' }}
          onClick={() => currentSection && duplicateSection(currentSection)}
        />
        <button
          id="delete_section"
          data-id={currentSection?.id}
          className="hide icon-trash ahu-header-buttons  btn-red ka-has-tooltip"
          {...{ tooltip: 'Delete' }}
          onClick={() => showDeletePopup(null)}
        />
      </div>

      <div className={`popup-wrapper delete-section-popup width-small${popupOpen ? ' active' : ''}`}>
        <div className="popup-content">
          <h2 className="popup-heading">Delete Section?</h2>
          <div>
            <div className="clearfix popup-content-part">
              {/* section.toolbar.js:199-211 — three texts: multi / "ER Wheel" /
                  single (double space after "Component" verbatim). */}
              <p>
                {multi
                  ? 'If you choose to Delete, Cabinet, Component & Accessories Data of the Sections will be lost and will not be recoverable.'
                  : String(popupSection?.name ?? '') === 'ER Wheel'
                    ? 'If you choose to Delete, Cabinet, Component & Accessories Data of both Sections will be lost and will not be recoverable.'
                    : 'If you choose to Delete, Cabinet, Component  & Accessories Data of the Section will be lost and will not be recoverable.'}
              </p>
              {/* section.toolbar.js:183-197 — UPPERCASED name + tag; EMPTY on
                  multi-select. */}
              <span className="section-tag-value">
                {multi
                  ? ''
                  : popupSection
                    ? `${String(popupSection.name ?? '').toUpperCase()}${
                        popupSection.tag ? ` : ${popupSection.tag}` : ''
                      }`
                    : ''}
              </span>
            </div>
            <div className="popup-control-buttons">
              <button
                className="delete not-highlighted-btn"
                data-id={popupSection?.id}
                name="delete_section_btn"
                onClick={doDelete}
              >
                DELETE
              </button>
              <button
                className="default-btn highlighted-btn"
                name="cancel_delete_section_btn"
                onClick={hideDeletePopup}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* "Can't duplicate dual-tunnel sections." (error_insert_component.popup.tmpl). */}
      {dupError != null && (
        <div className="popup-wrapper width-small active">
          <div className="popup-content">
            <h2 className="popup-heading">Duplicate Section</h2>
            <div>
              <div className="clearfix popup-content-part">
                <p>Can&apos;t duplicate dual-tunnel sections.</p>
                <span className="section-tag-value">{dupError} </span>
              </div>
              <div className="popup-control-buttons">
                <button
                  className="default-btn btn cancel"
                  name="cancel"
                  onClick={() => setDupError(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SectionToolbarChrome;
